import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { PageHero, CtaBand, PrimaryButton } from "../components/Primitives";
import { Seo } from "../components/Seo";
import { SERVICE_IMAGES } from "../lib/assets";

export default function Services() {
  const { t } = useLang();
  const navigate = useNavigate();
  const items = t("services.items");

  return (
    <div data-testid="services-page">
      <Seo pageKey="services" path="/services" />
      <PageHero overline={t("services.heroOverline")} title={t("services.title")} intro={t("services.intro")} />

      <section className="relative pb-8">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {items.map((s, i) => (
            <Reveal key={i} delay={0.05} className="group grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-white/10 py-14 md:py-16">
              <div className="md:col-span-4">
                <div className="relative aspect-[4/5] overflow-hidden border border-white/10 bg-kenosis-black">
                  <img
                    src={SERVICE_IMAGES[i]}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-85 transition-all duration-[1.2s] ease-out group-hover:scale-105 group-hover:opacity-100"
                  />
                  <span className="absolute left-4 top-4 font-serif text-2xl text-kenosis-gold">0{i + 1}</span>
                </div>
              </div>
              <div className="md:col-span-4">
                <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight text-kenosis-ivory">{s.name}</h2>
                <p className="mt-3 font-sans text-xs uppercase tracking-[0.2em] text-kenosis-gold">{s.duration}</p>
                <p className="mt-6 max-w-md font-sans text-base font-light leading-relaxed text-kenosis-muted">{s.desc}</p>
                <ul className="mt-6 space-y-2">
                  {s.features.map((f, k) => (
                    <li key={k} className="flex items-center gap-3 font-sans text-sm text-kenosis-ivory/70">
                      <span className="h-1 w-1 rounded-full bg-kenosis-gold/70" />{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:col-span-4">
                <div className="border border-white/10 bg-kenosis-surface p-8 md:p-10 h-full flex flex-col justify-between">
                  <div>
                    <p className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-kenosis-muted">{s.priceNote}</p>
                    <p className="mt-4 font-serif text-4xl md:text-5xl font-light text-kenosis-ivory">{s.price}</p>
                  </div>
                  <div className="mt-10">
                    <PrimaryButton testid={`service-apply-${i}`} onClick={() => navigate("/apply")}>{t("common.cta")}</PrimaryButton>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="border-l-2 border-kenosis-gold/50 pl-6">
            <p className="max-w-3xl font-serif text-2xl md:text-3xl font-light italic leading-snug text-kenosis-ivory/90 [text-wrap:balance]">
              {t("services.limited")}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-white/10 pt-8">
            <div className="md:col-span-3">
              <p className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-kenosis-gold">
                {t("services.pricingHeading")}
              </p>
            </div>
            <p className="md:col-span-9 font-sans text-sm md:text-base font-light leading-relaxed text-kenosis-muted/85 [text-wrap:balance]">
              {t("services.pricingNote")}
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
